import { CheckCircleOutlined, LinkOffOutlined, PauseCircleOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip, { type MetaChipTone } from "../../../../../components/UIToolkit/Badges/MetaChip";
import { SystemSwitch } from "../../../../../components/UIToolkit/Clickable/SystemSwitch";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SettingRow } from "../../../../../components/UIToolkit/molecules/SettingRow";
import { useAuth } from "../../../../../contexts/AuthProvider";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { agencySourceLabel, getAgencyIntegration } from "../../../../../utils/agencyIntegrations";
import type { AgencySource } from "../../../../../utils/models";
import { useSetAgencyIntegrationActive } from "../hooks";
import { AgencyDeactivationDialog } from "./AgencyDeactivationDialog";
import { AgencyReactivationDialog } from "./AgencyReactivationDialog";

/**
 * Where one agency's switch stands, as three states that must stay distinguishable.
 *
 * `unmapped` is the ABSENCE of an `agencyIntegrations` entry — the department was never wired to this
 * agency. It is emphatically NOT the same as `inactive`, which is a deliberate decision somebody made
 * and can undo. Collapsing the two is how "we turned Nursa off on the 17th" becomes indistinguishable
 * from "Nursa was never set up here", which is the confusion this whole ticket exists to end.
 */
export type AgencyToggleState = "unmapped" | "active" | "inactive";

const STATE_CHIP: Record<AgencyToggleState, { tone: MetaChipTone; icon: ReactNode; labelKey: string; tooltipKey: string }> = {
	active: {
		tone: "success",
		icon: <CheckCircleOutlined fontSize="small" />,
		labelKey: "department.config.integrations.agency.status.active",
		tooltipKey: "department.config.integrations.agency.status.active.tooltip",
	},
	inactive: {
		tone: "warning",
		icon: <PauseCircleOutlined fontSize="small" />,
		labelKey: "department.config.integrations.agency.status.inactive",
		tooltipKey: "department.config.integrations.agency.status.inactive.tooltip",
	},
	unmapped: {
		tone: "neutral",
		icon: <LinkOffOutlined fontSize="small" />,
		labelKey: "department.config.integrations.agency.status.unmapped",
		tooltipKey: "department.config.integrations.agency.status.unmapped.tooltip",
	},
};

/**
 * What the switch currently DOES, per state — state, never history.
 *
 * The row carries no audit fields any more: who moved this switch and when is the Activity Log's
 * answer (`hris.updated_by` + the `staff_change_log` trigger). So the `inactive` copy says what is
 * not happening rather than half-naming a change it can no longer attribute — an "Off since …" with
 * no date and no name is an audit trail that failed, printed as a setting description.
 */
const STATE_DESCRIPTION_KEY: Record<AgencyToggleState, string> = {
	active: "department.config.integrations.agency.toggle.description.active",
	inactive: "department.config.integrations.agency.toggle.description.inactive",
	unmapped: "department.config.integrations.agency.toggle.description.unmapped",
};

/**
 * Whether this department's `source` integration is on, off, or was never mapped.
 *
 * Reads the `agencyIntegrations` ROW, deliberately not `getAgencyFacilityIdMap`. The row is the source
 * of truth after PR1, and at least one real department is mapped through the row while its legacy
 * `nursa_facility_id` column is NULL — gating on the column would render that department's switch as
 * "map a Facility ID first", permanently disabled, which is the one department that most needs to be
 * turned back on.
 */
export const useAgencyToggleState = (source: AgencySource): AgencyToggleState => {
	const { chosenDepartment } = useChosenDepartment();
	const integration = getAgencyIntegration(chosenDepartment, source);
	if (!integration) return "unmapped";
	return integration.active ? "active" : "inactive";
};

/**
 * The section header's answer to "is this agency on for this department?" — the one question the
 * ticket exists to make answerable at a glance. Lives beside the switch it describes so the two
 * cannot disagree about what the three states are.
 *
 * Rendered UNCONDITIONALLY, unlike the switch. It is a pure read off `/user_info.agencyIntegrations`
 * and calls no gated endpoint, so there is nothing for the rollout flag to protect — and gating it
 * would mean a department deactivated during rollout has an Integrations tab that says nothing at all,
 * which is exactly the pre-DAT-885 state this feature exists to end.
 */
export const AgencyIntegrationStatusChip: FC<{ source: AgencySource }> = ({ source }) => {
	const { t } = useTranslation();
	const state = useAgencyToggleState(source);
	const { tone, icon, labelKey, tooltipKey } = STATE_CHIP[state];
	return (
		<MetaChip
			label={t(labelKey)}
			icon={icon}
			tone={tone}
			tooltip={t(tooltipKey, { agency: agencySourceLabel(source) })}
			dataTestId={`dept-config-agency-status-${source}`}
		/>
	);
};

/**
 * The per-department on/off switch for one agency marketplace (DAT-885).
 *
 * **Not a form field.** It has its own endpoint, takes effect immediately, and is `disabled` while the
 * configuration form is dirty — the same doctrine as the Nursa webhook button and the HRIS resync. The
 * alternative, riding the config PUT, would reproduce the fragile post-save arming the Nursa facility
 * id already needs (`forms/hooks.ts:987-1003`), for a control whose side effects are vendor calls.
 *
 * **Never hidden by ROLE.** Non-support users get it `disabled` with a tooltip saying why, which IS the
 * read-only view of a boolean — hiding it from them would answer "is Nursa on?" with silence for
 * everyone except support, and that question is the entire ticket.
 *
 * It carries no rollout flag. An earlier draft hid it behind `agency_integration_toggle_enabled`;
 * that flag is gone because support-only already bounds who can reach it, both of its side-effecting
 * paths reuse code the "Update webhook" button already exposes un-gated, and a flag that has to
 * exist in LaunchDarkly before the feature works at all is a second way for it to be broken.
 *
 * `source` is the only differing prop: both agencies get the same control, the same copy (one
 * `{{agency}}`-interpolated namespace) and the same two dialogs.
 */
export const AgencyIntegrationToggle: FC<{ source: AgencySource; isDirty: boolean }> = ({ source, isDirty }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const isSupport = !!userInfo?.support;
	const agency = agencySourceLabel(source);
	const integration = getAgencyIntegration(chosenDepartment, source);
	const state = useAgencyToggleState(source);
	const [deactivateOpen, setDeactivateOpen] = useState(false);
	const [reactivateOpen, setReactivateOpen] = useState(false);
	const { mutate: setActive, isPending } = useSetAgencyIntegrationActive();

	// Ordered by what the operator has to fix FIRST, so the tooltip always names the nearest obstacle
	// rather than the most severe one. Support comes before dirty: a non-support user saving the form
	// would still not be able to move this switch.
	const disabledReason = !integration
		? t("department.config.integrations.agency.toggle.blocked.unmapped", { agency })
		: !isSupport
			? t("department.config.integrations.agency.toggle.blocked.support", { agency })
			: isDirty
				? t("department.config.integrations.agency.toggle.blocked.dirty", { agency })
				: undefined;

	const description = t(STATE_DESCRIPTION_KEY[state], { agency });

	return (
		<Box
			// A hairline under the row, and nothing else. Every other control in these sections is a plain
			// `SettingRow`, and this one GOVERNS them — without a separator it reads as the first of four
			// peers rather than the master switch above the three settings it silences. Tinting or boxing
			// it instead would make one row of one settings page a style island; a divider says the same
			// thing in the page's own vocabulary. The colour is `theme.palette.divider`, so it re-tints
			// with the brand rather than pinning a grey.
			//
			// `:not(:last-child)` so the rule is self-correcting: a divider separates this row from what
			// follows it, and a section that renders the switch ALONE would otherwise end in a hairline
			// hanging off the bottom of its panel, separating it from nothing.
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 1,
				width: "100%",
				"&:not(:last-child)": { pb: 2, borderBottom: `1px solid ${theme.palette.divider}` },
			})}
			data-testid={`dept-config-agency-toggle-${source}`}
		>
			<SettingRow
				label={t("department.config.integrations.agency.toggle.label", { agency })}
				description={description}
				control={
					<SystemSwitch
						checked={state === "active"}
						disabled={disabledReason !== undefined || isPending}
						loading={isPending}
						// `SystemSwitch` wraps itself in the tooltip, which is what makes a DISABLED switch
						// hoverable at all — a disabled MUI control swallows pointer events.
						tooltip={disabledReason}
						ariaLabel={t("department.config.integrations.agency.toggle.label", { agency })}
						onChange={(event) => (event.target.checked ? setReactivateOpen(true) : setDeactivateOpen(true))}
						dataTestId={`dept-config-agency-toggle-switch-${source}`}
					/>
				}
				dataTestId={`dept-config-agency-toggle-row-${source}`}
			/>
			{/* `open` rather than `&&`: the hint collapses its height in step with the fade, so turning the
			    integration back on does not make everything below it jump by the hint's full height. */}
			<InlineHint tone="warning" open={state === "inactive"} dataTestId={`dept-config-agency-inactive-hint-${source}`}>
				{t("department.config.integrations.agency.toggle.inactiveHint", { agency })}
			</InlineHint>
			<AgencyDeactivationDialog
				open={deactivateOpen}
				source={source}
				confirmLoading={isPending}
				onClose={() => setDeactivateOpen(false)}
				onConfirm={(unpostOpenShifts, impact) =>
					setActive(
						{ source, active: false, unpostOpenShifts, impact },
						// `onSuccess`, not `onSettled`: no optimistic flip (the transition makes real vendor
						// calls, so the switch must not move before they land), and a FAILED attempt leaves the
						// dialog open with the acknowledgement still ticked, so retrying is one click rather than
						// re-reading four counts and re-consenting. Cancel is still there for giving up.
						{ onSuccess: () => setDeactivateOpen(false) },
					)
				}
			/>
			<AgencyReactivationDialog
				open={reactivateOpen}
				source={source}
				confirmLoading={isPending}
				onClose={() => setReactivateOpen(false)}
				onConfirm={() =>
					setActive(
						// `unpostOpenShifts` is meaningless on the way ON, and the server ignores it. Sent
						// explicitly anyway so no default on either side has to decide it.
						{ source, active: true, unpostOpenShifts: false },
						{ onSuccess: () => setReactivateOpen(false) },
					)
				}
			/>
		</Box>
	);
};
